import { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@ridereserv/shared/classnames';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/Modal.tsx
var FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(", ");
var bodyScrollLockCount = 0;
var bodyOverflowBeforeModal = "";
function lockBodyScroll() {
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeModal = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  bodyScrollLockCount += 1;
  return () => {
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) {
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = bodyOverflowBeforeModal;
      }
      bodyOverflowBeforeModal = "";
    }
  };
}
function useEscapeKey(onEscape) {
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onEscapeRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
}
function Modal({
  onClose,
  children,
  labelledBy,
  className,
  overlayClassName,
  "aria-label": ariaLabel,
  dismissible = true
}) {
  const panelRef = useRef(null);
  const requestClose = () => {
    if (dismissible) {
      onClose();
    }
  };
  useEscapeKey(requestClose);
  useEffect(() => lockBodyScroll(), []);
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel) {
      const preferred = panel.querySelector("[data-autofocus]");
      const firstFocusable = preferred ?? panel.querySelector(FOCUSABLE_SELECTOR);
      (firstFocusable ?? panel).focus();
    }
    return () => {
      previouslyFocused?.focus();
    };
  }, []);
  const handleKeyDown = (event) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || active === panel) {
        event.preventDefault();
        last?.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  return createPortal(
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn("fixed inset-0 z-50 flex items-center justify-center p-4", overlayClassName),
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              "data-testid": "modal-backdrop",
              className: "bg-overlay fixed inset-0",
              onClick: requestClose,
              "aria-hidden": "true"
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              ref: panelRef,
              role: "dialog",
              "aria-modal": "true",
              "aria-labelledby": labelledBy,
              "aria-label": ariaLabel,
              tabIndex: -1,
              onKeyDown: handleKeyDown,
              className: cn(
                "relative z-10 max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto focus:outline-none",
                className
              ),
              children
            }
          )
        ]
      }
    ),
    document.body
  );
}
function useDebouncedValue(value, delayMs = 300, immediateKey) {
  const [debounced, setDebounced] = useState(value);
  const previousImmediateKeyRef = useRef(immediateKey);
  useEffect(() => {
    if (!Object.is(previousImmediateKeyRef.current, immediateKey)) {
      previousImmediateKeyRef.current = immediateKey;
      setDebounced(value);
      return;
    }
    const timeout = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timeout);
    };
  }, [value, delayMs, immediateKey]);
  return debounced;
}
function useTableSort() {
  const [sort, setSort] = useState(null);
  const toggleSort = useCallback((column) => {
    setSort((previousSort) => {
      if (previousSort?.sortBy !== column) {
        return { sortBy: column, sortOrder: "asc" };
      }
      if (previousSort.sortOrder === "asc") {
        return { sortBy: column, sortOrder: "desc" };
      }
      return null;
    });
  }, []);
  const directionOf = useCallback(
    (column) => sort?.sortBy === column ? sort.sortOrder : null,
    [sort]
  );
  return { sort, toggleSort, directionOf };
}

export { Modal, useDebouncedValue, useTableSort };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map